import PropTypes from "prop-types";
import React, { useMemo } from "react";
import { useForm } from "react-hook-form";
import CommPrefs from "../components/CommPrefs";
import Groups from "../components/Groups";
import UserFields from "../components/UserFields";
import Submit from "../components/Submit";
import PasswordValidation from "../components/PasswordValidation";
import ErrorBoundary from "../components/ErrorBoundary";
import SafeSignal from "./SafeSignal";
import { useTranslation, Trans } from "react-i18next";
import RegistrationErrorList from "./RegistrationErrorList";

const STAGES = {
  Error: -1,
  Loading: 0,
  Activate: 1,
  Spinner: 2,
  EnterPin: 3,
  SkipPin: 4,
  Done: 5,
};

const SPECIAL_GROUPS = ["SafeSignal"];

const RegistrationForm = ({ responseData, user, postSubmit, customData, pinError, formError, stage, publicGroups, style }) => {
  const { t } = useTranslation();
  const safetyEnrollmentEnabled = Boolean(responseData?.enable_safesignal);
  const safetyEnrollmentRequired = Boolean(safetyEnrollmentEnabled && responseData?.style?.require_safesignal_enrollment);
  const {
    control,
    register,
    watch,
    handleSubmit,
    formState: { errors },
    setValue,
    getValues,
  } = useForm({
    defaultValues: {
      enrolling_in_safety: safetyEnrollmentRequired ? true : responseData?.enrolling_in_safety || false,
    },
    // Ensure disabled fields are included in form data and validation
    shouldUnregister: false,
  });

  const watchCountry = watch("user.country");

  // If enrollment is required, force the form value to true (also ensures it gets submitted)
  React.useEffect(() => {
    if (safetyEnrollmentRequired) {
      setValue("enrolling_in_safety", true);
    }
  }, [safetyEnrollmentRequired, setValue]);

  // Set admin_only field values when form loads so they're included in submission
  React.useEffect(() => {
    customData.forEach((field) => {
      if (field.admin_only) {
        const fieldValue = field.is_extended ? user.fields.additional_data?.[field.internal_name] : user.fields[field.internal_name];

        if (fieldValue !== undefined && fieldValue !== null && fieldValue !== "") {
          const fieldKey = field.is_extended ? `additionalData.${field.internal_name}` : `user.${field.internal_name}`;
          setValue(fieldKey, fieldValue);
        }
      }
    });
  }, [customData, user, setValue]);

  const isSmsRegistrationEnabled = responseData?.is_sms_registration_enabled;
  const userFields = useMemo(() => {
    const uf = customData
      .filter((c) => !c.is_extended)
      .sort((c1, c2) => c1.pinning - c2.pinning)
      .map((field) => (field.internal_name === "email" && !isSmsRegistrationEnabled ? { ...field, required: true } : field));

    if (uf[0]?.customer) {
      // add fake language field to the end of the list
      const languageField = {
        id: 999999999, // works but i don't like this fake id
        customer: uf[0].customer,
        admin_only: false,
        pinning: 99999999,
        in_use: true,
        created: "2025-08-01T00:00:00.000000Z",
        updated: "2025-08-01T00:00:00.000000Z",
        label: t("registration.language_preference_label"),
        internal_name: "preferred_language",
        field_type: "text",
        choices: null,
        is_extended: false,
        show_filter: true,
        filter_expression: "preferred_language",
        priority: 99999999,
        hidden: false,
        required: false,
        aliases: [],
        delimiter: ",",
        data_mask: false,
        tooltip: t("registration.language_preference_tooltip"),
      };
      uf.push(languageField);
    }
    return uf;
  }, [customData]);

  const customFields = useMemo(() => customData.filter((c) => c.is_extended).sort((c1, c2) => c1.priority - c2.priority), [customData]);

  const groups = useMemo(() => publicGroups.filter((x) => !SPECIAL_GROUPS.includes(x.name)), [publicGroups]);

  const isUnregistered = useMemo(() => (user.fields.id ? !user.fields.is_active : false), [user]);

  return (
    <div id="form-holder">
      <form id="user-form" onSubmit={handleSubmit(postSubmit)} autoComplete="off">
        <div id="user-data">
          <h2>{t("registration.confirm_or_edit_header")}</h2>
          <div id="fields">
            <UserFields
              fields={userFields}
              control={control}
              register={register}
              errors={errors}
              prefix="user"
              userData={user.fields}
              watchCountry={watchCountry}
            />
            <UserFields
              fields={customFields}
              control={control}
              register={register}
              errors={errors}
              prefix="additionalData"
              userData={user.fields.additional_data}
            />
            <div className="clear" />
          </div>
        </div>
        <div id="groups" className={groups.length > 0 ? "" : "collapse"} data-cy="groups">
          <h2>{t("registration.groups_header")}</h2>
          <Groups
            groups={groups}
            register={register}
            setValue={setValue}
            checkedGroups={user.groups}
            errors={errors}
            hierarchyVisible={responseData.style.display_hierarchy}
            hierarchyFolded={responseData.style.hierarchy_starts_folded}
          />
        </div>
        <div id="comm-prefs" data-cy="comm-prefs">
          <h2>{t("registration.contact_prefs_header")}</h2>
          <CommPrefs prefs={responseData.communications} register={register} fields={userFields.map((f) => f.internal_name)} setValue={setValue} />
          <div data-cy="opt-in-sms-info" className="info">
            <Trans i18nKey="registration.opt_in_sms_info" values={{ brand: responseData.customer_name }} />
          </div>
        </div>
        {(responseData.style.show_password || isUnregistered) && (
          <div className="password-wrapper">
            {responseData.enable_sso && style.show_password ? (
              <div data-cy="password-sso" className="info">
                {t("registration.sso_info")}
              </div>
            ) : (
              isUnregistered || (
                <div data-cy="password-optional" className="info">
                  {t("registration.password_optional_info")}
                </div>
              )
            )}
            {(style.show_password || (!responseData.enable_sso && isUnregistered)) && (
              <PasswordValidation
                register={register}
                errors={errors}
                getValues={getValues}
                watch={watch}
                requirePassword={!responseData.enable_sso && isUnregistered}
              />
            )}
          </div>
        )}
        <hr />
        {safetyEnrollmentEnabled && (
          <SafeSignal
            stage={stage}
            register={register}
            errors={errors}
            watch={watch}
            control={control}
            validate={stage !== STAGES.Activate}
            responseData={responseData}
            userData={user.fields}
          />
        )}
        <div className="clear" />
        <div id="terms" className={user.fields.tos_accepted > 0 ? "collapse" : ""}>
          <input
            type="checkbox"
            id="tos_accepted"
            name="tos_accepted"
            {...register("tos_accepted", {
              required: !user.fields.tos_accepted,
            })}
            defaultChecked={false}
            data-cy="tos_accepted"
          />
          <label htmlFor="tos_accepted">
            <Trans
              i18nKey="registration.tos_with_link"
              components={{
                a: <a href="https://www.alertmedia.com/legal/terms-of-service/" target="_blank" aria-label={t("registration.terms_of_service")} />,
              }}
            />
          </label>
        </div>
        <div id="action">
          <Submit stage={stage} register={register} />
          <br />
          <div className="errorlist" data-cy="error-list">
            <ErrorBoundary
              fallback={
                <p data-cy="error-fallback" className="errorlist">
                  {t("registration.error_generic")}
                  <br />
                </p>
              }
            >
              <RegistrationErrorList formError={formError} pinError={pinError} errors={errors} customData={customData} />
            </ErrorBoundary>
          </div>
        </div>
        <div id="terms-links">
          <a href="https://www.alertmedia.com/legal/end-user-terms-of-service/">{t("registration.terms_of_service")}</a>
          &nbsp;| &nbsp;
          <a href="https://www.alertmedia.com/privacy-policy/">{t("registration.privacy_policy")}</a>
        </div>
      </form>
    </div>
  );
};

RegistrationForm.propTypes = {
  customData: PropTypes.array,
  formError: PropTypes.func,
  pinError: PropTypes.string,
  postSubmit: PropTypes.func,
  publicGroups: PropTypes.array,
  responseData: PropTypes.shape({
    communications: PropTypes.any,
    enable_safesignal: PropTypes.bool,
    enable_sso: PropTypes.bool,
    enrolling_in_safety: PropTypes.bool,
    is_sms_registration_enabled: PropTypes.bool,
    style: PropTypes.shape({
      display_hierarchy: PropTypes.bool,
      hierarchy_starts_folded: PropTypes.bool,
      require_safesignal_enrollment: PropTypes.bool,
      show_password: PropTypes.bool,
    }),
  }),
  stage: PropTypes.number,
  style: PropTypes.shape({
    show_password: PropTypes.bool,
  }),
  user: PropTypes.shape({
    fields: PropTypes.shape({
      additional_data: PropTypes.any,
      id: PropTypes.number,
      is_active: PropTypes.bool,
      tos_accepted: PropTypes.number,
    }),
    groups: PropTypes.array,
  }),
};

export default RegistrationForm;
